<template>
  <div class="question-list p-4">
    <!-- Loading -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Caricamento...</span>
      </div>
      <div class="loading-text">Caricamento in corso...</div>
    </div>

    <!-- Toast -->
    <ToastDialog :title="toastTitle" :message="toastMessage" :error="toastError" />

    <!-- Header UOC -->
    <div class="bg-white p-2 d-flex justify-content-between align-items-center mb-3 border rounded">
      <div>
        Data prossima UOC: {{ nextUocDate ? formatDate(nextUocDate) : 'N/A' }}
      </div>
      <div class="d-flex gap-2 ms-auto position-relative">
        <div class="position-relative d-inline-block">
          <button class="btn btn-outline-secondary btn-sm rounded-pill px-3 py-2" @click="goToUocArchive">
            Archivio UOC
          </button>

          <span v-if="hasNewUoc"
            class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            NEW
          </span>
        </div>
        <div class="position-relative d-inline-block">
          <button v-if="canScheduleUoc && isUOCManager" class="btn btn-primary btn-sm rounded-pill px-3 py-2"
            @click="openNewUoc">
            FISSA UOC
          </button>
        </div>
        <div class="position-relative d-inline-block">
          <button v-if="canSetUoc && isUOCManager" class="btn btn-warning btn-sm rounded-pill px-3 py-2"
            @click="rescheduleUoc">
            RIPROGRAMMA UOC
          </button>
        </div>

        <div class="d-flex gap-2 ms-auto position-relative">
          <!-- Pulsante calendario -->
          <!--<button class="btn btn-outline-secondary" @click="toggleCalendar">
            <i class="bi bi-calendar-event"></i>
          </button>-->

          <div v-show="true" class="calendar-popup">
            <VueDatePicker is-inline :read-only="true" :hide-time-picker="true" :enable-time-picker="false"
              :auto-apply="true" :show-action-buttons="false" :day-class="highlightUocDays" :day-content="dayContent"
              @mousemove="onHoverDay" @month-change="onMonthChange" />
          </div>
        </div>
      </div>
    </div>


    <div class="d-flex align-items-center justify-content-between mb-3">
      <div class="d-flex align-items-center gap-3">
        <div v-if="isUOCManager" class="toggle-3d">
          <input type="checkbox" id="viewToggle" class="toggle-switch" v-model="roomView" true-value="uoc"
            false-value="attesa">
          <label for="viewToggle" class="toggle-label">
            <span class="toggle-option toggle-left">
              Sala d’attesa ({{ salaAttesaCount }})
            </span>
            <span class="toggle-option toggle-right">
              Sala UOC ({{ salaUocCount }})
            </span>
          </label>
        </div>

        <div v-else-if="isUOCMember" class="single-uoc-pill">
          <span class="pill-text">
            Sala UOC ({{ salaUocCount }})
          </span>
        </div>

        <button class="btn btn-outline-secondary btn-sm p-1" style="border-color: #D3D3D3; font-size: 0.8rem;"
          @click="showFilters = !showFilters">
          <i class="bi bi-funnel-fill"></i>
        </button>
      </div>


      <button v-if="isUOCManager" class="btn btn-sm px-4 rounded-pill fw-semibold"
        :class="roomView === 'attesa' ? 'btn-primary' : 'btn-warning'" :disabled="selectedQuestions.length === 0"
        @click="moveQuestions">
        {{ roomView === 'attesa'
          ? 'SPOSTA IN SALA UOC'
          : 'SPOSTA IN SALA D’ATTESA' }}
      </button>

    </div>

    <transition name="slide-fade" @enter="enter" @leave="leave">
      <CommonUOCFilters v-show="showFilters" :allTopics="allTopics" :allLegislations="allLegislations"
        :allArticles="allArticles" :allRegions="allRegions" :allYears="allYears" v-model:selectedTopics="selectedTopics"
        v-model:selectedLegislation="selectedLegislation" v-model:selectedArticles="selectedArticles"
        :isGestore="isGestore || isLegalExpert" @update:selectedLegislation="onLegislationChange"
        @update:selectedTopics="val => selectedTopics = val" @update:selectedArticles="val => selectedArticles = val"
        @filter="applyFilter" />
    </transition>

    <div class="mb-3 d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center">
        <label class="me-2 mb-0">Elementi per pagina:</label>
        <select class="form-select w-auto" v-model.number="pagination.perPage" @change="loadQuestions(1)">
          <option v-for="n in [10, 25, 50, 100, 500]" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>
      <button class="btn btn-outline-secondary btn-sm rounded-pill px-3 py-2" @click="downloadPdf"
        :disabled="isDownloading || selectedQuestions.length === 0">
        <span v-if="!isDownloading">
          <i class="bi bi-file-earmark-pdf me-1"></i> Scarica PDF
        </span>
        <span v-else>
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Generazione in corso...
        </span>
      </button>
    </div>

    <UOCTable :questions="questions" :pagination="pagination" :sort-field="sortField" :sort-direction="sortDirection"
      :state-icons="stateIcons" @page-change="loadQuestions" @sort="handleSort" @open-detail="openQuestionDetail"
      @change-view="changeView" @update:selected-questions="selectedQuestions = $event" />

    <!-- Form UOC -->
    <ModalView v-model="showScheduleModal" title="Pianifica UOC" icon="calendar">
      <UOCForm v-model="selectedUoc" :errors="uocErrors" :successMessage="uocSuccess" @save="saveUoc"
        @delete="deleteUoc" @close="showScheduleModal = false" :can-save="canSaveUoc" />
    </ModalView>
  </div>
</template>


<script>
import ToastDialog from '../../../components/common/ToastDialog.vue';
import CommonUOCFilters from './partials/CommonUOCFilters.vue';
import UOCTable from './partials/UOCTable.vue';
import toastable from '../../../mixins/toastable';
import ModalView from '../../../components/common/ModalView.vue';
import UOCForm from './partials/UOCForm.vue';
import { ref, computed } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { Tooltip } from 'bootstrap';

export default {
  name: 'SalaAttesaSalaUOCView',
  components: {
    ToastDialog,
    CommonUOCFilters,
    UOCTable,
    ModalView,
    UOCForm,
    VueDatePicker
  },
  mixins: [toastable],
  data() {
    return {
      selectedQuestion: {
        object: '',
        text: '',
        topics: []
      },
      selectedQuestions: [],
      showDetailModal: false,
      isGestore: false,
      isLegalExpert: false,
      questions: [],
      pagination: { currentPage: 1, perPage: 10, total: 0, lastPage: 1 },
      sortField: 'created_at',
      sortDirection: 'desc',
      allTopics: [],
      allLegislations: [],
      allArticles: [],
      selectedTopics: [],
      selectedLegislation: null,
      selectedArticles: [],
      toastTitle: '',
      toastMessage: '',
      toastError: false,
      stateIcons: {
        INVIATO: { icon: 'bi-plus-circle', activeColor: '#0d6efd', inactiveColor: 'gray', label: 'Nuovo' },
        ASSEGNATO: { icon: 'bi-person-check', activeColor: '#20c997', inactiveColor: 'gray', label: 'Assegnato' },
        RISPOSTA_IN_BOZZA: { icon: 'bi-pencil-square', activeColor: '#0dcaf0', inactiveColor: 'gray', label: 'Risposta in bozza' },
        EVASO: { icon: 'bi-check-circle', activeColor: '#198754', inactiveColor: 'gray', label: 'Evaso' },
        IN_SALA_ATTESA: { icon: 'bi-hourglass', activeColor: '#ffc107', inactiveColor: 'gray', label: 'In sala d’attesa' },
        IN_ESAMINA_UOC: { icon: 'bi-search', activeColor: '#6610f2', inactiveColor: 'gray', label: 'Assegnato a UOC' },
        NON_ASSEVERATO: { icon: 'bi-shield-exclamation', activeColor: '#fd7e14', inactiveColor: 'gray', label: 'Non asseverato' },
        ASSEVERATO: { icon: 'bi-shield-check', activeColor: '#0dcaf0', inactiveColor: 'gray', label: 'Asseverato' },
        NON_PUBBLICATO: { icon: 'bi-eye-slash', activeColor: '#dc3545', inactiveColor: 'gray', label: 'Non pubblicato' },
        PUBBLICATO: { icon: 'bi-eye', activeColor: '#0d6efd', inactiveColor: 'gray', label: 'Pubblicato' }
      },
      filterId: '',
      filterObject: '',
      filterOrganization: '',
      filterText: '',
      filterRegion: [],
      filterYear: [],
      allYears: [],
      isLoading: true,
      isDownloading: false,
      showFilters: false,
      filterCreatedFrom: '',
      filterCreatedTo: '',
      filterSendingFrom: '',
      filterSendingTo: '',
      allSearchIds: [],
      nextUocDate: null,
      showScheduleModal: false,
      selectedUoc: {
        id: null,
        date: '',
        type: 'generica',
        object: '',
        description: ''
      },
      uocErrors: null,
      uocSuccess: null,
      showUocModal: false,
      uocSessions: [],
      uocTooltips: {},
      selectedUoc: null,
      showCalendar: false,
      selectedDate: null,
      canScheduleUoc: true,
      uocInfo: null,
      uocInfoHour: '',
      hoveredUocDay: null,
      hoveredTooltip: null,
      currentMonth: new Date(),
      roomView: 'attesa',
      salaAttesaCount: 0,
      salaUocCount: 0,
      hasNewUoc: false
    };
  },
  watch: {
    roomView() {
      this.selectedQuestions = [];
      this.loadQuestions(1);
    },
    showCalendar(newVal) {
      if (newVal) {
        this.$nextTick(() => this.attachUocTooltips());
      }
    },
    uocSessions: {
      handler() {
        this.$nextTick(() => this.attachUocTooltips());
      },
      immediate: true
    },
    currentMonth() {
      this.$nextTick(() => this.attachUocTooltips());
    },
  },
  computed: {
    isUOCMember() {
      const perms = this.$root.user?.permissions || [];
      return perms.includes('view-uoc') && !perms.includes('manage-uoc');
    },

    isUOCManager() {
      return this.$root.user?.permissions?.includes('manage-uoc');
    },
    hasSalaUoc() {
      return this.salaUocCount > 0;
    },

    hasFutureUoc() {
      return !!this.nextUocDate;
    },

    canScheduleUoc() {
      return this.hasSalaUoc && !this.hasFutureUoc;
    },

    canSetUoc() {
      return this.hasFutureUoc;
    },
    uocDatesSet() {
      return new Set(
        (this.uocSessions || []).map(u => new Date(u.date).toISOString().slice(0, 10))
      );
    }
  },
  methods: {
    async moveQuestions() {
      try {
        await this.$root.$api.post('/uoc/questions/move', {
          question_ids: this.selectedQuestions,
          direction: this.roomView === 'attesa' ? 'to_uoc' : 'to_attesa'
        });

        this.selectedQuestions = [];
        await this.loadQuestions(1);
      } catch (e) {
        this.showErrorToast('Errore', 'Operazione non riuscita');
      }
    },
    onHoverDay(e) {
      const cell = e.target.closest('.dp__cell_inner');
      if (!cell) {
        this.hoveredTooltip = null;
        return;
      }
      const dayNum = parseInt(cell.textContent.trim());
      if (isNaN(dayNum)) {
        this.hoveredTooltip = null;
        return;
      }

      const date = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), dayNum);
      console.log('Giorno sotto il mouse:', date.toDateString()); // 

      const key = date.toDateString();
      this.hoveredTooltip = this.uocTooltips[key] || null;
      console.log('hoveredTooltip' + this.hoveredTooltip)
    },
    showUocTooltip(dateStr) {
      this.hoveredUocDay = dateStr;
    },
    hideUocTooltip() {
      this.hoveredUocDay = null;
    },
    attachUocTooltips() {
      const uocDays = this.$el.querySelectorAll('.uoc-past, .uoc-future');
      uocDays.forEach(dayEl => {
        const dayNum = parseInt(dayEl.textContent.trim());
        if (isNaN(dayNum)) return;

        dayEl.replaceWith(dayEl.cloneNode(true));
        const newDayEl = this.$el.querySelector(`#dp-${dayNum}`) || dayEl;

        newDayEl.addEventListener('mouseenter', () => {
          const date = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), dayNum);
          const key = date.toDateString();
          this.hoveredTooltip = this.uocTooltips[key] || null;
        });
        newDayEl.addEventListener('mouseleave', () => {
          this.hoveredTooltip = null;
        });
      });
    },

    dayContent(day) {
      const key = day.date.toDateString();
      //const tooltip = this.uocTooltips[key] || ''; // orario UOC se esiste

      const tooltip = 'Tooltip';

      return `<span title="Disabilita Autenticazione a due fattori">${day.day}</span>`
    }
    ,
    onDayClick(event) {
      const date = event.detail.date;
      const found = this.uocSessions.find(uoc => {
        const uocDate = new Date(uoc.date.replace(' ', 'T'));
        return (
          uocDate.getDate() === date.getDate() &&
          uocDate.getMonth() === date.getMonth() &&
          uocDate.getFullYear() === date.getFullYear()
        );
      });

      if (found) {
        const uocDate = new Date(found.date.replace(' ', 'T'));
        this.uocInfo = {
          ...found,
          hour: `${String(uocDate.getHours()).padStart(2, '0')}:${String(uocDate.getMinutes()).padStart(2, '0')}`
        };
      } else {
        this.uocInfo = null;
      }
    },
    rescheduleUoc() {
      this.uocErrors = null;
      this.uocSuccess = null;
      if (this.nextUocDate) {
        const uoc = this.uocSessions.find(u => new Date(u.date).getTime() === new Date(this.nextUocDate).getTime());
        if (uoc) {
          this.selectedUoc = {
            id: uoc.id || null,
            date: uoc.date || '',
            type: uoc.type || 'generica',
            object: uoc.object || '',
            description: uoc.notes || '',
            conference_link : uoc.conference_link || ''
          };
          this.showScheduleModal = true;
        }
      }
    },
    toggleCalendar() {
      this.showCalendar = !this.showCalendar;
    },
    highlightUocDays(date) {
      const day = date.getDate();
      const month = date.getMonth();
      const year = date.getFullYear();

      const found = this.uocSessions.find(uoc => {
        const uocDate = new Date(uoc.date.replace(' ', 'T'));

        return (
          uocDate.getDate() === day &&
          uocDate.getMonth() === month &&
          uocDate.getFullYear() === year
        );
      });

      if (!found) return '';

      const uocDate = new Date(found.date.replace(' ', 'T'));
      const now = new Date();

      const key = uocDate.toDateString();
      this.uocTooltips[key] = `${String(uocDate.getHours()).padStart(2, '0')}:${String(uocDate.getMinutes()).padStart(2, '0')}`;

      if (uocDate < now) {
        return 'uoc-past';
      } else {
        return 'uoc-future';
      }
    },
    onDateSelected(date) {
      this.selectedUoc.date = date;
      this.showCalendar = false;
      this.showScheduleModal = true;
    },
    async loadUocSessions() {
      const res = await this.$root.$api.get('/uoc-sessions')
      this.uocSessions = res.data
      this.$nextTick(() => {
        this.attachUocTooltips();
      });
    },
    openCreateUoc(date) {
      this.selectedUoc = {
        date,
        type: 'generica',
        object: '',
        description: ''
      }
      this.showUocModal = true
    },
    openEditUoc(id) {
      this.selectedUoc = this.uocSessions.find(u => u.id == id)
      this.showUocModal = true
    },
    goToUocArchive() {
      this.hasNewUoc = false;
      this.$emit('change-view', 'UOCArchiveView');
    },
    openNewUoc() {
      this.uocErrors = null;
      this.uocSuccess = null;
      this.selectedUoc = {
        id: null,
        date: '',
        type: 'generica',
        object: '',
        description: ''
      };
      this.showScheduleModal = true;
    },

    async openExistingUoc(uoc) {
      this.selectedUoc = { ...uoc }; // copia i dati esistenti
      this.showScheduleModal = true;
    },

    async saveUoc(uocData) {
      this.isLoading = true;
      try {
        if (uocData.id) {
          await this.$root.$api.put(`/uoc/sessions/${uocData.id}`, uocData);
        } else {
          const res = await this.$root.$api.post('/uoc/sessions', uocData);
          this.hasNewUoc = true;
          const newUocId = res.data?.data?.id || res.data?.id;

          if (newUocId) {
            localStorage.setItem('last_uoc_id', newUocId);
          }
        }
        this.showScheduleModal = false;
        await this.loadUocSessions();
        this.loadNextUoc();
        await this.loadQuestions(1);
        this.showSuccessToast('UOC salvata con successo');
      } catch (err) {
        this.uocErrors = err.response?.data?.errors || { general: ['Errore durante il salvataggio'] };
      } finally {
        this.isLoading = false;
      }
    },
    async deleteUoc(uocData) {
      try {
        await this.$root.$api.delete(`/uoc/sessions/${uocData.id}`);
        this.showScheduleModal = false;
        await this.loadQuestions();
        this.loadNextUoc();
        this.showSuccessToast('UOC eliminata con successo');
      } catch (err) {
        this.uocErrors = err.response?.data?.errors || { general: ['Errore durante la cancellazione'] };
      }
    },
    async loadNextUoc() {
      try {
        const res = await this.$root.$api.get('/uoc/next-session');
        this.nextUocDate = res.data.date || null;
      } catch (err) {
        this.nextUocDate = 'N/A';
      }
    },
    formatDate(isoDate) {
      if (!isoDate) return '';
      const date = new Date(isoDate);

      return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    changeView(viewName, id) {
      console.log('Evento ricevuto:', viewName, id);
      this.$emit('change-view', viewName, id);
    },
    async loadQuestions(page = 1) {
      this.isLoading = true;
      this.selectedQuestions = [];
      try {
        const params = {
          page: page,
          per_page: this.pagination.perPage,
          sortField: this.sortField,
          sortDirection: this.sortDirection,
          room: this.roomView,
          topics: this.selectedTopics.join(','),
          articles: this.selectedArticles.join(','),
          legislation_id_v: this.selectedLegislation ?? '',
          id: this.filterId || '',
          object: this.filterObject || '',
          organization_desc: this.filterOrganization || '',
          text: this.filterText || '',
          region: this.filterRegion.join(',') || '',
          year: this.filterYear.join(',') || '',
          created_from: this.filterCreatedFrom || '',
          created_to: this.filterCreatedTo || '',
          sending_from: this.filterSendingFrom || '',
          sending_to: this.filterSendingTo || ''
        };
        const response = await this.$root.$api.get('/uoc/questions-uoc', { params });
        const questionsWithArticleTitles = response.data.data.map(q => {
          const newQ = { ...q };

          const articlesArray = this.$root.contents?.articles || [];

          newQ.articles = (q.articles || []).map(a => {
            const content = articlesArray.find(c => c.id === a.id);
            if (!content || !content.versions || !content.versions.length) return { ...a, title: null };

            const lastVersion = content.versions.reduce(
              (prev, curr) => (curr.id > prev.id ? curr : prev),
              content.versions[0]
            );

            return { ...a, title: lastVersion.title };
          });

          return newQ;
        });
        this.questions = questionsWithArticleTitles;

        this.pagination = {
          currentPage: response.data.current_page,
          perPage: response.data.per_page,
          total: response.data.total,
          lastPage: response.data.last_page
        };
        this.allSearchIds = response.data.allSearchIds || [];
        this.salaAttesaCount = response.data.sala_attesa_count;
        this.salaUocCount = response.data.sala_uoc_count;
      } catch (error) {
        this.showErrorToast('Errore', 'Errore nel caricamento dei quesiti');
      } finally {
        this.isLoading = false;
      }
    },
    async loadAllTopics() {
      const response = await this.$root.$api.get('/questions/topics');
      this.allTopics = response.data;
    },
    async loadAllLegislations() {
      //this.allLegislations = Object.values(this.$root.dlgs || {});
      const legislations = Object.values(this.$root.dlgs || {});

      const activeLegislation = legislations.filter(l => l.end === null);

      this.allLegislations = [
        {
          id: 0,
          begin: null,
          end: null,
          legislation: null,
          published_at: null,
          deleted_at: null,
          created_at: null,
          updated_at: null,
          title: "D.lgs 50/2016"
        },
        ...activeLegislation
      ];
    },
    async loadAllRegions() {
      const response = await this.$root.$api.get('/questions/regions');
      this.allRegions = response.data;
    },
    async loadAvailableYears() {
      const response = await this.$root.$api.get('/questions/years');
      this.allYears = response.data;
    },
    handleSort({ field, direction }) {
      this.sortField = field;
      this.sortDirection = direction;
      this.loadQuestions(1);
    },
    applyFilter(filters) {
      this.filterId = filters.id || '';
      this.filterObject = filters.object || '';
      this.filterOrganization = filters.organization || '';
      this.filterText = filters.text || '';
      this.filterRegion = filters.region || '';
      this.filterYear = filters.year || '';
      this.filterState = filters.state || '';
      this.filterCreatedFrom = filters.created_from || '';
      this.filterCreatedTo = filters.created_to || '';
      this.filterSendingFrom = filters.sending_from || '';
      this.filterSendingTo = filters.sending_to || '';
      this.loadQuestions(1);
    },
    async onLegislationChange(newLegId) {
      this.selectedLegislation = newLegId;
      this.selectedArticles = [];
      if (!newLegId) {
        this.allArticles = [];
        return;
      }

      const allArticles = Object.values(this.$root.contents?.articles || [])
        .filter(a => !a.deleted_at && a.versions && a.versions.length > 0);

      const legislation = this.allLegislations.find(l => l.id === newLegId);

      if (!legislation) {
        this.allArticles = [];
        return;
      }

      this.allArticles = this.filterArticlesByLegislationDate(allArticles, legislation);

      this.allArticles.sort((a, b) => a.id - b.id);

      this.allArticles = this.allArticles.map(a => ({
        ...a,
        title: a.versions[a.versions.length - 1]?.title || ''
      }));

    },
    async downloadPdf() {
      this.isDownloading = true;
      try {


        const idsToPrint = this.selectedQuestions.length > 0
          ? this.selectedQuestions
          : this.allSearchIds;

        console.log(idsToPrint);

        const params = {
          ids: idsToPrint.join(','),
          sortField: this.sortField,
          sortDirection: this.sortDirection,
          topics: this.selectedTopics.join(','),
          articles: this.selectedArticles.join(','),
          legislation_id_v: this.selectedLegislation ?? '',
          id: this.filterId || '',
          object: this.filterObject || '',
          organization_desc: this.filterOrganization || '',
          text: this.filterText || '',
          region: this.filterRegion.join(',') || '',
          year: this.filterYear.join(',') || '',
          state: Array.isArray(this.filterState) ? this.filterState.join(',') : '',
          created_from: this.filterCreatedFrom || '',
          created_to: this.filterCreatedTo || '',
          sending_from: this.filterSendingFrom || '',
          sending_to: this.filterSendingTo || ''
        };

        if (this.selectedQuestions.length > 0) {
          params.ids = this.selectedQuestions.join(',');
        }
        await this.$root.$api.get('/questions/pdf/exportUOC', { params });
        this.showSuccessToast('PDF in preparazione. Ti verrà notificato quando sarà pronto.');
        this.$root.triggerPdfPolling();

      } catch (error) {
        this.showErrorToast('Errore', 'Impossibile scaricare il PDF');
      } finally {
        this.isDownloading = false;
      }
    },
    filterArticlesByLegislationDate(allArticles, legislation, searchDate = null) {
      const activeDate = searchDate ? new Date(searchDate) : new Date();

      return allArticles
        .map(article => {
          const allVersions = article.versions.map(v => ({ ...v.version, frontendVersionId: v.id }));
          let activeVersions = allVersions.filter(v => {
            const begin = new Date(v.begin);
            const end = v.end ? new Date(v.end) : null;
            if (end) return activeDate >= begin && activeDate <= end;
            return activeDate >= begin;
          });
          if (activeVersions.length === 0 && allVersions.length > 0) {
            const sortedByBeginDesc = allVersions.sort((a, b) => new Date(b.begin) - new Date(a.begin));
            activeVersions = [sortedByBeginDesc[0]];
          }
          let filteredVersions = activeVersions;
          if (activeVersions.length > 1) {
            const mainVersion = activeVersions.find(v => {
              const begin = new Date(v.begin);
              const end = v.end ? new Date(v.end) : null;
              return (!end && begin <= activeDate) || (end && begin <= activeDate && end >= activeDate);
            }) || activeVersions[0];
            filteredVersions = activeVersions.filter(v => v.title === mainVersion.title);
          }
          const finalVersions = article.versions.filter(av =>
            filteredVersions.some(fv => fv.id === av.version.id)
          );
          return { ...article, versions: finalVersions };
        })
        .filter(article => article.versions.length > 0)
        .sort((a, b) => a.id - b.id);
    }
  },
  async mounted() {
    const roles = this.$root?.user?.roles || [];
    //this.isGestore = roles.some(role => role.id === 2);
    //this.isLegalExpert = roles.some(role => role.id === 3);
    this.isGestore = this.$root?.user?.permissions.includes('assign-question');
    this.isLegalExpert = (this.$root?.user?.permissions.includes('view-questions') && 
    this.$root?.user?.permissions.includes('view-question')) || (this.$root?.user?.permissions.includes('view-uoc'));
    this.$nextTick(() => {
      this.attachUocTooltips();
    });
    if (this.isUOCMember) {
      this.roomView = 'uoc';
    }
    await this.loadUocSessions();
    await this.loadNextUoc();
    await this.loadAvailableYears();
    await this.loadAllRegions();
    await this.loadAllTopics();
    await this.loadAllLegislations();
    await this.loadQuestions();
  },
  async created() {
    console.log("caricamento articoli");
    if (!this.$root.contents) {
      await this.$root.fetchContents()
    }
  }
};
</script>
<style scoped>
.question-list {
  padding: 2rem;
}

/* Tabella base */
.table th,
.table td {
  vertical-align: middle;
}

.question-list table {
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  table-layout: fixed;
  width: 100%;
}

.question-list table thead {
  background-color: #0d6efd !important;
  color: white !important;
}

.question-list ::v-deep(table thead th) {
  text-align: center !important;
  vertical-align: middle !important;
}

/* Hover */
.question-list table tbody tr:hover {
  background-color: #f1f5fb;
  transition: background-color 0.2s ease;
}

/* Celle */
.question-list ::v-deep(.table-card table th),
.question-list ::v-deep(.table-card table td) {
  text-align: center;
  padding: 0.75rem 1rem;
}

/* Colonne specifiche 
.question-list ::v-deep(.table-card table th:nth-child(1)) {
  width: 5%;
}
.question-list ::v-deep(.table-card table th:nth-child(2)) {
  width: 10%;
}
.question-list ::v-deep(.table-card table th:nth-child(3)) {
  width: 25%;
}
.question-list ::v-deep(.table-card table th:nth-child(4)) {
  width: 40%;
  text-align: left;
}
.question-list ::v-deep(.table-card table th:nth-child(5)) {
  width: 15%;
}
.question-list ::v-deep(.table-card table th:nth-child(6)) {
  width: 5%;
}
  */

/* Celle centrali truncate */
.question-list ::v-deep(.table-card table td:nth-child(3)) {
  width: 15%;
  max-width: 15%;
  overflow: hidden;
}

.question-list ::v-deep(.table-card table td:nth-child(4)) {
  text-align: left;
}

.question-list ::v-deep(.table-card table td:nth-child(3) .text-truncate-ellipsis) {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.loading-text {
  margin-top: 1rem;
  font-weight: bold;
  color: #0d6efd;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.1s ease;
  overflow: hidden;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  max-height: 500px;
  opacity: 0;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  max-height: 500px;
  opacity: 1;
}

.countdown-view {
  flex: 1;
  display: flex;
  gap: 1rem;
  /*background: #f5f5f5;*/
  padding: 1rem;
  height: 75vh;
  /* prendi tutta l’altezza del contenitore genitore */
}

/** .countdown-column {
  flex: 1;
  background: #f8f9fa;
  border-radius: 0.25rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.questions-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
*/

.countdown-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 0.25rem;
  height: 100%;
}

.questions-wrapper {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.countdown-column h6 {
  text-align: center;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.question-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.question-card:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.avatar-circle {
  width: 33.06px !important;
  height: 33px !important;
  flex: 0 0 auto;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  color: #6c757d;
  background: white;
  border: 2px solid gray;
}

.avatar-circle.empty {
  background: #fff;
  border-color: #6c757d;
  color: #6c757d;
}

.status-box {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: white;
  font-weight: 600;
}

.status-yellow {
  background: #ffc107;
}

.status-blue {
  background: #0d6efd;
}

.status-purple {
  background: #6f42c1;
}

.status-gray {
  background: #6c757d;
}

.fc-col-header {
  min-width: 600px;
}

::v-deep(.uoc-day) {
  background-color: #198754 !important;
  color: white !important;
  border-radius: 50%;
}

::v-deep(.uoc-past) {
  background-color: #ffc107 !important;
  color: white !important;
  border-radius: 50%;
}

::v-deep(.uoc-future) {
  background-color: #198754 !important;
  color: white !important;
  border-radius: 50%;
}

.uoc-info-popup {
  position: relative;
  margin-top: 4px;
  background: #0d6efd;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  width: fit-content;
}

.calendar-popup {
  position: relative;
  width: 5rem !important;
  box-sizing: unset;
}

.calendar-popup ::v-deep(.dp__input_icon) {
  cursor: pointer;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--dp-icon-color);
}

.calendar-popup ::v-deep(.dp__input_icon) {
  margin: 0;
}

.toggle-3d {
  position: relative;
  width: 22rem;
  height: 2rem;
  perspective: 500px;
}

.toggle-3d input.toggle-switch {
  display: none;
}

.toggle-3d .toggle-label {
  display: block;
  width: 22rem;
  height: 2rem;
  background-color: #e9ecef;
  border-radius: 50px;
  padding: 5px;
  /* spazio interno per effetto “toggle flottante” */
  box-shadow: inset 0 4px 6px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.2);
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border: 2px solid #0A2342;
}

.toggle-3d .toggle-option {
  position: absolute;
  width: 50%;
  height: 100%;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  color: white;
  transition: color 0.3s;
  pointer-events: none;
}

.toggle-3d .toggle-left {
  left: 0;
}

.toggle-3d .toggle-right {
  right: 0;
}

.toggle-3d .toggle-label::before {
  content: '';
  position: absolute;
  width: calc(50% - 10px);
  /* più piccolo del 50% per lasciare spazio bordo */
  height: calc(100% - 10px);
  top: 5px;
  left: 5px;
  background-color: #0A2342;
  border-radius: 50px;
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
  transition: left 0.3s;
}

.toggle-3d input.toggle-switch:checked+.toggle-label::before {
  left: calc(50% + 5px);
}

.toggle-3d input.toggle-switch:checked+.toggle-label .toggle-left {
  color: black;
}

.toggle-3d input.toggle-switch:not(:checked)+.toggle-label .toggle-right {
  color: black;
}

.single-uoc-pill .pill-text {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  font-size: 1rem;
  font-weight: 500;
  color: #ffffff;
  background: linear-gradient(135deg, #0a3d62, #1e3799);
  border-radius: 50px;
  white-space: nowrap;
}
</style>
